import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Package,
  Loader2,
  Send,
  FileText,
  ExternalLink,
  Minus,
  Plus,
} from "lucide-react";
import { formatApiError } from "@/lib/errors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Slide-in shopping cart for quote requests.
 *
 * Mounted once at app shell level. Opens via the cart icon in the header.
 * Renders each line with quantity stepper + per-line note. On submit, the
 * backend splits items by manufacturer and creates a quote_request per group.
 */
export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    cart,
    drawerOpen,
    setDrawerOpen,
    itemCount,
    updateItem,
    removeItem,
    submit,
  } = useCart();

  const [submitting, setSubmitting] = useState(false);

  const items = cart.items || [];

  const handleSubmit = async (message) => {
    setSubmitting(true);
    try {
      const res = await submit(message);
      const count = res?.count || 0;
      toast.success(
        count > 1
          ? `Quote request sent to ${count} manufacturers. We'll be in touch within 1 business day.`
          : "Quote request sent. We'll be in touch within 1 business day."
      );
      setDrawerOpen(false);
      navigate("/quotes");
      return true;
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit quote request"));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] sm:max-w-[480px] flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="font-display tracking-tight">
            Quote cart {itemCount > 0 ? `(${itemCount})` : ""}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Add products as you search. We'll route the request to the right
            pricing contacts at your rep agency.
          </SheetDescription>
        </SheetHeader>

        <div
          className="flex-1 overflow-y-auto p-5 space-y-3"
          data-testid="cart-items-list"
        >
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 text-muted-foreground">
              <Package className="w-10 h-10 mb-3 text-slate-300" strokeWidth={1.25} />
              <div className="font-medium text-slate-700">Your cart is empty.</div>
              <div className="text-xs mt-1">
                Hit "Add to cart" on any product to start a quote request.
              </div>
            </div>
          ) : (
            items.map((it) => <CartLine key={it.id} item={it} onUpdate={updateItem} onRemove={removeItem} />)
          )}
        </div>

        {items.length > 0 && (
          <CartSubmitBlock
            itemCount={itemCount}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// Isolated block: owns its own `message` local state so keystrokes only
// re-render this small subtree. Previously the message state lived in
// CartDrawer, which caused every keystroke to re-render the whole cart
// (including every CartLine and the Radix Sheet chrome) — producing
// noticeable input lag once the cart had a few items in it.
const CartSubmitBlock = React.memo(function CartSubmitBlock({
  itemCount,
  submitting,
  onSubmit,
}) {
  const [message, setMessage] = useState("");
  const handleClick = async () => {
    const ok = await onSubmit(message);
    if (ok) setMessage("");
  };
  return (
    <div className="border-t border-border p-5 space-y-3 bg-slate-50">
      <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
        Message to your rep agency (optional)
      </label>
      <Textarea
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Need by Friday, jobsite is Charlotte NC, prefer 0-10V dimming…"
        className="rounded-sm text-sm"
        data-testid="cart-customer-message"
        maxLength={2000}
      />
      <Button
        onClick={handleClick}
        disabled={submitting}
        className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm h-11"
        data-testid="cart-submit-btn"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Send className="w-4 h-4 mr-2" />
        )}
        Request quote ({itemCount} {itemCount === 1 ? "item" : "items"})
      </Button>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data text-center">
        Your contact info is included automatically
      </div>
    </div>
  );
});

function CartLine({ item, onUpdate, onRemove }) {
  const product = item.product;
  if (!product) {
    return (
      <div
        className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800"
        data-testid="cart-line-missing"
      >
        <span>This product is no longer available.</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRemove(item.id)}
          aria-label="Remove"
          data-testid={`cart-remove-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    );
  }
  const mfr = product.manufacturer;
  const specUrl = product.spec_sheet_url;
  const fullSpec = specUrl
    ? specUrl.startsWith("http")
      ? specUrl
      : `${BACKEND_URL}${specUrl}`
    : null;

  const setQty = (n) => {
    if (n < 1) return;
    onUpdate(item.id, { quantity: n });
  };

  return (
    <div
      className="bg-white border border-border rounded-sm p-3"
      data-testid={`cart-line-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-data truncate">
            {mfr?.name}
          </div>
          <div className="font-display text-sm font-bold tracking-tight text-slate-900 truncate">
            {product.name}
          </div>
          {product.model_number && (
            <div className="text-[11px] font-mono-data text-slate-700 truncate">
              {product.model_number}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          aria-label="Remove from cart"
          className="text-muted-foreground hover:text-destructive transition-colors p-1"
          data-testid={`cart-remove-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-3">
        {/* Quantity stepper */}
        <div className="inline-flex items-center border border-border rounded-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setQty((item.quantity || 1) - 1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
            aria-label="Decrease quantity"
            data-testid={`cart-qty-decr-${item.id}`}
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            min={1}
            max={99999}
            value={item.quantity}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n) && n >= 1) setQty(n);
            }}
            className="w-12 h-8 text-center text-sm font-mono-data outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            data-testid={`cart-qty-input-${item.id}`}
          />
          <button
            type="button"
            onClick={() => setQty((item.quantity || 1) + 1)}
            className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
            aria-label="Increase quantity"
            data-testid={`cart-qty-incr-${item.id}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {fullSpec && (
          <a
            href={fullSpec}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-amber-600 underline underline-offset-2"
            data-testid={`cart-spec-${item.id}`}
          >
            <FileText className="w-3.5 h-3.5" />
            Spec
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      <div className="mt-3">
        <Label
          htmlFor={`cart-note-${item.id}`}
          className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data"
        >
          Describe what you need quoted
        </Label>
        <CartLineNote
          itemId={item.id}
          initialNote={item.note || ""}
          onSave={(next) => onUpdate(item.id, { note: next })}
        />
      </div>
    </div>
  );
}

/**
 * Per-line note editor. Keeps the current text in LOCAL state so typing is
 * instant — the server PATCH only fires on blur (or when the item changes
 * externally). Previously we were firing an API call on every keystroke,
 * which produced very noticeable typing lag with multiple items in the cart.
 */
function CartLineNote({ itemId, initialNote, onSave }) {
  const [value, setValue] = useState(initialNote);
  const lastSavedRef = React.useRef(initialNote);

  // If the item's server-side note changes (external update, cart refresh),
  // sync local state — but only when the field isn't currently being edited.
  useEffect(() => {
    if (document.activeElement?.id !== `cart-note-${itemId}`) {
      setValue(initialNote);
      lastSavedRef.current = initialNote;
    }
  }, [initialNote, itemId]);

  const flushIfChanged = () => {
    if (value !== lastSavedRef.current) {
      lastSavedRef.current = value;
      onSave(value);
    }
  };

  return (
    <>
      <Textarea
        id={`cart-note-${itemId}`}
        placeholder="Describe what you need: wattage, lumens, CCT, distribution, color, mounting, quantity — anything from the spec sheet's ordering matrix"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={flushIfChanged}
        className="mt-1 rounded-sm text-xs min-h-[88px]"
        maxLength={500}
        data-testid={`cart-note-${itemId}`}
      />
      <div className="mt-0.5 text-[10px] text-muted-foreground text-right font-mono-data">
        {value.length}/500
      </div>
    </>
  );
}
